/**
 * Shipping Company API
 *
 * API endpoints for shipping company management operations
 */

import dayjs from "dayjs";
import toast from "react-hot-toast";

import { nextCargoApi } from "../../baseApi";
import { shippingCompanyBindArgsSchema } from "./schemas";
import type {
  ListShippingCompanies,
  ShippingCompanies,
  ShippingCompany,
  ShippingCompanyBindArgs,
  ShippingCompanyCreate,
  ShippingCompanyEdit,
  ShippingCompanySearch
} from "./shippingCompany.types";

/**
 * Shipping Company API endpoints
 * Provides CRUD operations for shipping company management
 */
export const shippingCompanyApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Get all shipping companies
     * @returns Array of shipping companies sorted by creation date
     */
    getShippingCompanies: build.query<ShippingCompany[], void, ShippingCompany[]>({
      query: () => "/shipping-company",
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando transportadoras...",
          success: "Transportadoras carregadas com sucesso!",
          error: "Erro ao carregar transportadoras."
        });
      },
      transformResponse: (response) => {
        const sortByCreatedAt = response.sort((a, b) => dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix());
        return sortByCreatedAt;
      }
    }),

    /**
     * Get paginated list of shipping companies
     * @param params - Filter parameters
     * @param pageParam - Pagination parameters
     * @returns Paginated list of shipping companies
     */
    listShippingCompanies: build.infiniteQuery<
      ListShippingCompanies,
      { params?: App.Filters.Base<ShippingCompanies> },
      App.Filters.Pagination<ShippingCompanies>,
      ListShippingCompanies
    >({
      infiniteQueryOptions: {
        initialPageParam: { page: 0, pageSize: 20, sortDirection: "asc", sortField: "name" },
        getNextPageParam: (lastPage, _, lastPageParam) => {
          const nextPage = lastPageParam.page + 1;
          const remainingPages = lastPage?.total - nextPage;

          if (remainingPages <= 0) {
            return undefined;
          }

          return {
            ...lastPageParam,
            page: nextPage
          };
        },
        getPreviousPageParam: (_, __, firstPageParam) => {
          const prevPage = firstPageParam.page - 1;
          if (prevPage < 0) return undefined;

          return {
            ...firstPageParam,
            page: prevPage
          };
        }
      },
      query: ({ queryArg: { params }, pageParam }) => ({
        url: "/shipping-company",
        params: Object.assign({}, pageParam, params)
      })
    }),

    /**
     * Get a single shipping company by ID
     * @param id - Shipping company ID
     * @returns Full shipping company details
     */
    getShippingCompany: build.query<ShippingCompanies, Pick<ShippingCompanies, "id">>({
      query: ({ id }) => `/shipping-company/${id}`,
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando transportadora...",
          success: "Transportadora carregada com sucesso!",
          error: "Erro ao carregar transportadora."
        });
      }
    }),

    /**
     * Search shipping companies by query string
     * @param query - Search query
     * @returns Array of matching shipping companies
     */
    searchShippingCompanies: build.query<ShippingCompanySearch[], { query: string }>({
      query: ({ query }) => `/shipping-company/like/${query}`
      // onQueryStarted: (args, { queryFulfilled }) => {
      //   toast.promise(queryFulfilled, {
      //     loading: "Buscando transportadoras...",
      //     success: "Transportadoras carregadas com sucesso!",
      //     error: "Erro ao buscar transportadoras."
      //   });
      // }
    }),

    /**
     * Create a new shipping company
     * @param payload - Shipping company data
     * @returns void
     */
    createShippingCompany: build.mutation<void, ShippingCompanyCreate>({
      query: (body) => ({
        url: "/shipping-company/create",
        method: "POST",
        body: {
          id: 0,
          code: "",
          ...body
        }
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Cadastrando transportadora...",
          success: "Transportadora cadastrada com sucesso!",
          error: "Erro ao cadastrar transportadora."
        });
      }
    }),

    /**
     * Update an existing shipping company
     * @param payload - Shipping company data with id
     * @returns void
     */
    editShippingCompany: build.mutation<void, ShippingCompanyEdit>({
      query: ({ id, ...body }) => ({
        url: "/shipping-company/create",
        method: "POST",
        body: {
          id: id || null,
          code: "",
          ...body
        }
      }),
      onQueryStarted: ({ id }, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: id ? "Atualizando transportadora..." : "Criando Transportadora...",
          success: id ? "Transportadora atualizada com sucesso!" : "Transportadora criada com sucesso!",
          error: id ? "Erro ao atualizar Transportadora!" : "Erro ao criar Transportadora!"
        });
      }
    }),

    /**
     * Delete a shipping company
     * @param id - Shipping company ID to delete
     * @returns void
     */
    deleteShippingCompany: build.mutation<void, Pick<ShippingCompanies, "id">>({
      query: ({ id }) => ({
        url: `/shipping-company/delete/${id}`,
        method: "DELETE"
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Excluindo transportadora...",
          success: "Transportadora excluída com sucesso!",
          error: "Erro ao excluir transportadora."
        });
      }
    }),

    /**
     * Bind a shipping company to a company with driver groups
     * @param payload - Binding data with shipping company, company, and groups
     * @returns void
     */
    bindShippingCompany: build.mutation<void, ShippingCompanyBindArgs>({
      query: (body) => ({
        url: "/shipping-company/bind",
        method: "POST",
        body
      }),
      argSchema: shippingCompanyBindArgsSchema,
      skipSchemaValidation: true,
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Vinculando transportadora...",
          success: "Transportadora vinculada com sucesso!",
          error: "Erro ao vincular transportadora."
        });
      }
    })
  })
});

/**
 * Enhance endpoints with cache tag configuration
 */
shippingCompanyApi.enhanceEndpoints({
  addTagTypes: ["ShippingCompany", "ListShippingCompany"],
  endpoints: {
    getShippingCompanies: {
      providesTags: (result) =>
        result ? [...result.map(({ id }) => ({ type: "ShippingCompany" as const, id })), { type: "ShippingCompany", id: "LIST" }] : [{ type: "ShippingCompany", id: "LIST" }]
    },
    listShippingCompanies: {
      providesTags: (result) =>
        result
          ? [...result.pages.flatMap(({ data }) => data.map(({ id }) => ({ type: "ListShippingCompany" as const, id }))), { type: "ListShippingCompany", id: "LIST" }]
          : [{ type: "ShippingCompany", id: "LIST" }]
    },
    getShippingCompany: {
      providesTags: (result, error, { id }) => (id ? [{ type: "ShippingCompany", id }] : [{ type: "ShippingCompany", id: "LIST" }])
    },
    searchShippingCompanies: {
      providesTags: (result) =>
        result ? [...result.map(({ id }) => ({ type: "ShippingCompany" as const, id })), { type: "ShippingCompany", id: "LIST" }] : [{ type: "ShippingCompany", id: "LIST" }]
    },
    createShippingCompany: {
      invalidatesTags: (result, error) => (!error ? [{ type: "ShippingCompany", id: "LIST" }] : [])
    },
    editShippingCompany: {
      invalidatesTags: (result, error, { id }) => (!error ? [{ type: "ShippingCompany", id: "LIST" }, ...(id ? [{ type: "ShippingCompany" as const, id }] : [])] : [])
    },
    deleteShippingCompany: {
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: "ShippingCompany", id: "LIST" },
              { type: "ShippingCompany", id }
            ]
          : []
    },
    bindShippingCompany: {
      invalidatesTags: (result, error) => (!error ? ["ShippingCompany", "DriverGroup"] : [])
    }
  }
});
